import { type ComponentType, type ReactNode, type RefObject, createContext, useContext, useId, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { LANG_META, type Lang, type LocalizedText } from '@lp/shared';
import { Field, Input, Textarea, cx } from './form.tsx';

/** A localized field as the form's translate button sees it. */
export interface TranslatableField {
  label: string;
  value: LocalizedText;
  /** Rich text (HTML). */
  html?: boolean;
  maxLength?: number;
}

type Registered = TranslatableField & { onChange: (next: LocalizedText) => void };

/** What LocalizedForm hands the app's translate button. */
export interface LocalizedTranslateProps {
  /** The language tab being edited. */
  lang: Lang;
  languages: Lang[];
  /** The form's fields as they are now. */
  fields: () => Array<TranslatableField & { id: string }>;
  /** Merges translations into the fields, by field id. */
  fill: (texts: Record<string, LocalizedText>) => void;
}

/**
 * An optional translate button the app shows under every form's language tabs (the brand portal's AI 翻譯, which hides
 * itself when the brand lacks the add-on). Without a provider there is no button.
 */
export const LocalizedTranslate = createContext<ComponentType<LocalizedTranslateProps> | null>(null);

/**
 * One language tab bar per form (like the OpenCart product editor): every localized field below it
 * shows the active language, so a brand fills one language at a time on the same page.
 */
const ActiveLang = createContext<{ lang: Lang; languages: Lang[]; fields?: Map<string, RefObject<Registered>> }>({ lang: 'zh-Hant', languages: ['zh-Hant'] });

export function LocalizedForm({ languages, children, missing }: { languages: Lang[]; children: ReactNode; missing?: (lang: Lang) => boolean }) {
  const [lang, setLang] = useState<Lang>(languages[0] ?? 'zh-Hant');
  const active = languages.includes(lang) ? lang : (languages[0] ?? 'zh-Hant');
  // The localized fields below, for the translate button (each registers itself).
  const [fields] = useState(() => new Map<string, RefObject<Registered>>());
  const Translate = useContext(LocalizedTranslate);
  return (
    <ActiveLang.Provider value={{ lang: active, languages, fields }}>
      <div role="tablist" aria-label="語言" className="mb-5 flex gap-1 border-b border-line">
        {languages.map((l) => (
          <button
            key={l}
            type="button"
            role="tab"
            aria-selected={active === l}
            onClick={() => setLang(l)}
            className={cx(
              '-mb-px flex items-center gap-2 rounded-t-lg border px-4 py-2 text-[15px] transition-colors',
              active === l ? 'border-line border-b-white bg-white font-medium text-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            <span className="rounded bg-mint px-1.5 py-0.5 text-[11px] font-semibold text-ink-soft">{LANG_META[l].short}</span>
            {LANG_META[l].label}
            {missing?.(l) && <span className="size-1.5 rounded-full bg-coral" title="有欄位未填" />}
          </button>
        ))}
      </div>
      {Translate && languages.length > 1 && (
        <Translate
          lang={active}
          languages={languages}
          fields={() => [...fields].map(([id, f]) => ({ id, label: f.current.label, value: f.current.value, html: f.current.html, maxLength: f.current.maxLength }))}
          fill={(texts) => {
            // One field at a time, re-rendering in between, so a page whose onChange merges into a stale copy of its
            // draft still keeps the fields filled before.
            for (const [id, t] of Object.entries(texts)) {
              const f = fields.get(id)?.current;
              if (f && Object.keys(t).length) flushSync(() => f.onChange({ ...f.value, ...t }));
            }
          }}
        />
      )}
      {children}
    </ActiveLang.Provider>
  );
}

export function useActiveLang() {
  return useContext(ActiveLang);
}

/** Puts a field on its form's list for the translate button. */
function useTranslatable(field: Registered) {
  const { fields } = useContext(ActiveLang);
  const id = useId();
  const ref = useRef(field);
  ref.current = field;
  useLayoutEffect(() => {
    if (!fields) return;
    fields.set(id, ref);
    return () => void fields.delete(id);
  }, [fields, id]);
}

/** Includes a field drawn by another component (e.g. a rich-text editor) in the form's translation; renders nothing. */
export function Translatable(props: Registered) {
  useTranslatable(props);
  return null;
}

/** Which of the enabled languages have text, e.g. "繁 ✓ · 简 – · EN ✓". */
function Filled({ value, languages }: { value: LocalizedText; languages: Lang[] }) {
  if (languages.length < 2) return null;
  return (
    <span className="flex gap-1.5">
      {languages.map((l) => (
        <span key={l} className={value[l]?.trim() ? 'text-brand-700' : 'text-muted/60'}>
          {LANG_META[l].short}
          {value[l]?.trim() ? '✓' : '–'}
        </span>
      ))}
    </span>
  );
}

interface LocalizedFieldProps {
  label: ReactNode;
  value: LocalizedText;
  onChange: (next: LocalizedText) => void;
  required?: boolean;
  hint?: ReactNode;
  error?: string;
  placeholder?: string;
  maxLength?: number;
  /** A small button beside the label, e.g. the brand portal's 「AI 寫文案」. */
  action?: ReactNode;
}

function Aside({ value, languages, action }: { value: LocalizedText; languages: Lang[]; action?: ReactNode }) {
  if (!action) return <Filled value={value} languages={languages} />;
  return (
    <span className="flex items-center gap-3">
      {action}
      <Filled value={value} languages={languages} />
    </span>
  );
}

export function LocalizedInput({ label, value, onChange, required, hint, error, placeholder, maxLength, action }: LocalizedFieldProps) {
  const { lang, languages } = useActiveLang();
  useTranslatable({ label: typeof label === 'string' ? label : '', value, onChange, maxLength });
  return (
    <Field label={label} required={required} hint={hint} error={error} aside={<Aside value={value} languages={languages} action={action} />}>
      {(id) => (
        <Input
          id={id}
          lang={LANG_META[lang].htmlLang}
          value={value[lang] ?? ''}
          placeholder={placeholder}
          maxLength={maxLength}
          invalid={!!error}
          onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
        />
      )}
    </Field>
  );
}

export function LocalizedTextarea({ label, value, onChange, required, hint, error, placeholder, maxLength, rows = 4, action }: LocalizedFieldProps & { rows?: number }) {
  const { lang, languages } = useActiveLang();
  useTranslatable({ label: typeof label === 'string' ? label : '', value, onChange, maxLength });
  return (
    <Field label={label} required={required} hint={hint} error={error} aside={<Aside value={value} languages={languages} action={action} />}>
      {(id) => (
        <Textarea
          id={id}
          rows={rows}
          lang={LANG_META[lang].htmlLang}
          value={value[lang] ?? ''}
          placeholder={placeholder}
          maxLength={maxLength}
          invalid={!!error}
          onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
        />
      )}
    </Field>
  );
}

/** True when any enabled language of any of these fields is empty; feeds the red dot on a tab. */
export function missingIn(lang: Lang, ...texts: LocalizedText[]): boolean {
  return texts.some((t) => !t[lang]?.trim());
}
